import { CSVLink } from 'react-csv';
import { useSearchParams } from 'react-router-dom';
import { Node } from '@prisma/client';
import { Breadcrumb, Button, Input, Layout, Modal, Table } from 'antd';

import { NodeSupplierContractWithDetails } from '@app/domain-t3-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { PageLayout, Tables } from '@app/web-components';

import { useFindNodes } from '../../nodes';
import { useFindNodeSupplierContracts } from '../data';
import { useNodeSupplierContractActions } from '../libs';

export type LandingPageItem = NodeSupplierContractWithDetails;

export const LandingPage = () => {
  const [searchParams] = useSearchParams();
  const {
    getActionButton,
    getDeleteModalProps,
    getDetailsModalProps,
    toggleCreateModal,
    getCreateModalProps,
    getEditModalProps,
  } = useNodeSupplierContractActions();

  const nodeName = searchParams.get('node');

  const { data: nodeList } = useFindNodes<Node>({});

  const {
    getCreateButtonProps,
    getCSVLinkProps,
    getParentLayoutContentProps,
    getQuery,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
  } = Tables.useTableState<LandingPageItem>(
    'Node Supplier Contract',
    [
      {
        title: 'Action',
        width: 120,
        render: (_, record) => getActionButton(record),
      },
      {
        title: 'Supplier Name',
        dataIndex: 'supplierName',
        filterDropdown: Tables.customSearchFilterDropdownFactory('Search Supplier'),
        ellipsis: true,
        sorter: true,
      },
      {
        title: 'Supplier ID',
        dataIndex: ['supplier', 'externalId'],
      },
      {
        title: 'Node',
        dataIndex: ['node', 'name'],
        filters: nodeList?.data.map((node: Node) => ({
          text: node.name,
          value: node.name,
        })),
        filterSearch: true,
        defaultFilteredValue: nodeName ? [nodeName] : undefined,
        sorter: true,
      },
      {
        title: 'Contract',
        dataIndex: 'contractName',
        filterDropdown: Tables.customSearchFilterDropdownFactory('Search users'),
        sorter: true,
      },
      {
        title: 'Contract #',
        dataIndex: 'contractNumber',
        filterDropdown: Tables.customSearchFilterDropdownFactory('Search users'),
      },
      {
        title: 'Intel Signed Date',
        dataIndex: 'intelSignedDate',
        ellipsis: true,
        sorter: true,
        render: (date: string) => formatIsoDate(date, false),
      },
      {
        title: 'Updated By',
        dataIndex: ['updatedByUser', 'name'],
        ellipsis: true,
        sorter: true,
      },
      {
        title: 'Updated On',
        dataIndex: 'updatedOn',
        ellipsis: true,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
      },
      {
        title: 'Created By',
        dataIndex: ['createdByUser', 'name'],
        ellipsis: true,
        sorter: true,
        filterDropdown: Tables.customSearchFilterDropdownFactory('Search users'),
      },
      {
        title: 'Created On',
        dataIndex: 'createdOn',
        ellipsis: true,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
        defaultSortOrder: 'descend',
      },
    ],
    {
      globalSearchFields: ['supplierName', 'contractName', 'contractNumber'],
      disableSession: !!nodeName,
    },
  );

  const queryResult = useFindNodeSupplierContracts<LandingPageItem>(
    getQuery({
      include: {
        createdByUser: true,
        updatedByUser: true,
        node: true,
        supplier: true,
      },
    }),
  );

  return (
    <>
      <Modal {...getCreateModalProps()} />
      <Modal {...getEditModalProps()} />
      <Modal {...getDeleteModalProps()} />
      <Modal {...getDetailsModalProps()} />
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: 'Home' }, { title: 'Admin' }, { title: 'Node Supplier Contracts' }]}
      />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title="Node Supplier Contracts" />
        <div
          {...getToolbarProps({
            items: [
              <Input
                {...getSearchInputProps()}
                placeholder="Search by supplier name, contract name, contract number"
              />,
              <Button {...getCreateButtonProps()} onClick={() => toggleCreateModal()} />,
              <Button {...getReloadButtonProps()} onClick={() => queryResult.refetch()} />,
              <Button {...getResetFiltersButtonProps()} />,
              <CSVLink {...getCSVLinkProps(queryResult.data?.data)} />,
            ],
          })}
        />
        <Table {...getTableProps(queryResult)} />
      </Layout.Content>
    </>
  );
};
